import { useEffect, useState } from 'react'
import { Caption, COLORS, Panel, Badge } from './ui'

const MODELS = ['GPT-4o', 'Gemini', 'Claude', 'Qwen-VL', 'Llama Vision']

// Two distinct architectures people lump together. CLIP aligns image+text in a
// shared space; VLMs feed visual tokens into a language model.
export function Multimodal2D() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const panelWidth = mobile ? '100%' : 320

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      alignItems: 'center', justifyContent: 'center', gap: mobile ? 8 : 16, padding: mobile ? '80px 20px 90px' : '90px 24px 110px',
      fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center', color: COLORS.text,
    }}>
      <Badge color={COLORS.purple}>Multimodal — connecting pixels & language</Badge>

      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: mobile ? 10 : 20, flexWrap: 'wrap', justifyContent: 'center', width: mobile ? '100%' : undefined, maxWidth: mobile ? 360 : undefined }}>
        {/* CLIP */}
        <Panel glow={COLORS.indigo} style={{ width: panelWidth, padding: mobile ? 10 : 16, display: 'flex', flexDirection: 'column', gap: mobile ? 6 : 10 }}>
          <div style={{ fontSize: mobile ? 12.5 : 14.5, fontWeight: 800, color: COLORS.indigoLight }}>CLIP — alignment</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Box label="🖼 image" sub="encoder" color={COLORS.purple} />
            <span style={{ fontSize: mobile ? 10.5 : 12.5, color: COLORS.textDim, textAlign: 'center' }}>shared<br />space</span>
            <Box label="“a cat” " sub="text encoder" color={COLORS.blue} />
          </div>
          {!mobile && (
            <div style={{ height: 30, position: 'relative' }}>
              <div style={{ position: 'absolute', inset: 0, borderRadius: 8, background: 'radial-gradient(circle, rgba(129,140,248,0.3), transparent 70%)' }} />
              <div style={{ position: 'absolute', left: '38%', top: 8, width: 10, height: 10, borderRadius: '50%', background: COLORS.purple }} />
              <div style={{ position: 'absolute', left: '54%', top: 11, width: 10, height: 10, borderRadius: '50%', background: COLORS.blue }} />
            </div>
          )}
          <div style={{ fontSize: 12.5, color: COLORS.textDim, lineHeight: 1.5 }}>
            Contrastive training pulls matching image–caption pairs together. Enables zero-shot classification & search.
          </div>
        </Panel>

        {mobile && <div style={{ color: COLORS.indigo, fontSize: 22, opacity: 0.6 }}>↓</div>}

        {/* VLM */}
        <Panel glow={COLORS.pink} style={{ width: panelWidth, padding: mobile ? 10 : 16, display: 'flex', flexDirection: 'column', gap: mobile ? 6 : 10 }}>
          <div style={{ fontSize: mobile ? 12.5 : 14.5, fontWeight: 800, color: COLORS.pink }}>VLM — vision into an LLM</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12.5, flexWrap: 'wrap' }}>
            <Box label="🖼" sub="vision enc." color={COLORS.purple} />
            <span style={{ color: COLORS.textFaint }}>→</span>
            <Box label="proj" sub="→ tokens" color={COLORS.green} />
            <span style={{ color: COLORS.textFaint }}>→</span>
            <Box label="🧠 LLM" sub="reasons" color={COLORS.amber} />
          </div>
          <div style={{ fontSize: 13, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: COLORS.textDim, background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: 8 }}>
            “What is the cat doing?” → <span style={{ color: '#fff' }}>“Sitting in sunlight.”</span>
          </div>
          <div style={{ fontSize: 12.5, color: COLORS.textDim, lineHeight: 1.5 }}>
            Image tokens are injected into a language model, which then reasons and answers in words.
          </div>
        </Panel>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
        {MODELS.map(m => (
          <span key={m} style={{ fontSize: 12.5, padding: '4px 10px', borderRadius: 6, color: COLORS.text, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)' }}>{m}</span>
        ))}
      </div>

      <Caption>
        “Multimodal” covers two different ideas: <strong>CLIP</strong> aligns images and text in one embedding
        space; <strong>VLMs</strong> (GPT-4o, Gemini, Claude) feed visual tokens into a language model so it can
        reason about what it sees.
      </Caption>
    </div>
  )
}

function Box({ label, sub, color }) {
  return (
    <div style={{ padding: '6px 10px', borderRadius: 8, background: `${color}18`, border: `1px solid ${color}55`, textAlign: 'center' }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color }}>{label}</div>
      <div style={{ fontSize: 9, color: COLORS.textFaint, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{sub}</div>
    </div>
  )
}
